// Chamadas do Mapa de prospecção (ações `pm_*` em /comercial/propostas-geradas).
// Contrato: docs/mapa-prospeccao.md (se divergir, vale o doc). Uma função por ação;
// o erro vem com a frase do servidor (`erroDe`), que é o que a tela mostra.
// Os tipos do contrato moram em ./regras (puro, testado); aqui só os reexporta.
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fraseDeFalha } from '../comercial-onde-ganha/ondeGanhaRegras';
import { juntarPaginas, type Bbox, type CamadaPartes, type LicencaIndiciosCompleto, type Obter, type Prospecto } from './regras';

export type {
  Bbox, Camada, CamadaPartes, CampanhaResumo, Filtros, Obter, ParteCamada, Prioridade, Prospecto, Rotulos,
} from './regras';

async function post<T>(body: Record<string, unknown>, timeout = 30000): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, body, { headers: { Authorization: token }, timeout });
  return (r.data?.body ? JSON.parse(r.data.body) : r.data) as T;
}

interface ErroHttp {
  response?: { status?: number; data?: { error?: unknown; erro?: unknown } };
  code?: string;
  message?: string;
}

export const erroDe = (e: unknown): string => {
  const x = e as ErroHttp;
  const d = x?.response?.data;
  const doServidor = d?.error || d?.erro;
  if (typeof doServidor === 'string' && doServidor.trim()) return doServidor;
  return fraseDeFalha(x?.response?.status ?? null, !!x?.response, x?.code, x?.message);
};

/** 409 = outra pessoa mudou a linha antes. */
export const ehConflito = (e: unknown): boolean => (e as ErroHttp)?.response?.status === 409;
/** 403 = sem a permissão da ação. */
export const ehSemAcesso = (e: unknown): boolean => (e as ErroHttp)?.response?.status === 403;

// ── Ações ────────────────────────────────────────────────────────────────────
const MAX_PAGINAS = 40;

/**
 * Sem `campanha`, o servidor devolve a que tem mais linhas. O servidor manda os prospectos em páginas
 * (cada resposta cabe nos 6 MB da Lambda); aqui as páginas são pedidas em sequência e juntadas.
 */
export async function obter(campanha?: string): Promise<Obter> {
  const base = { action: 'pm_obter', ...(campanha ? { campanha } : {}) };
  const primeira = await post<Obter>({ ...base, offset: 0 }, 60000);
  const seguintes: Obter[] = [];
  let ultima = primeira;
  let offset = primeira.prospectos.length;
  while (ultima.restantes > 0 && ultima.prospectos.length > 0 && seguintes.length < MAX_PAGINAS) {
    const pagina = await post<Obter>({ ...base, campanha: primeira.campanha ?? campanha, offset }, 60000);
    seguintes.push(pagina);
    offset += pagina.prospectos.length;
    ultima = pagina;
  }
  return juntarPaginas(primeira, seguintes);
}

/**
 * Partes (polígonos) da camada que cruzam `bbox` e ainda não vieram (`ja` = chaves já carregadas).
 * `restantes` > 0: pedir de novo com `ja` acrescido das chaves recebidas.
 */
export const camadaPartes = (codigo: string, bbox: Bbox, ja: string[]): Promise<CamadaPartes> =>
  post<CamadaPartes>({ action: 'pm_camada_partes', codigo, bbox, ja }, 60000);

export const atualizar = (p: {
  id: number; status?: string; observacoes?: string; visto: { atualizado_em: string };
}): Promise<{ prospecto: Prospecto }> => post<{ prospecto: Prospecto }>({ action: 'pm_atualizar', ...p });

/**
 * Texto do PDF da licença e os indícios COMPLETOS (com os trechos), que não viajam no obter.
 * `texto` e `indicios` null = o sistema ainda não leu esta licença.
 */
export const licencaTexto = (id: number): Promise<{ id: number; texto: string | null; indicios: LicencaIndiciosCompleto | null }> =>
  post<{ id: number; texto: string | null; indicios: LicencaIndiciosCompleto | null }>({ action: 'pm_licenca_texto', id });

/**
 * A IA lê a licença contra a diretriz e grava a leitura; devolve a linha inteira. Síncrona (até ~20 s no servidor).
 * Leitura já pronta só é refeita com `refazer` (e o servidor recusa com 429 se foi lida há menos de um minuto).
 */
export const lerLicenca = (id: number, refazer = false): Promise<{ prospecto: Prospecto }> =>
  post<{ prospecto: Prospecto }>({ action: 'pm_licenca_ler', id, refazer }, 60000);
